import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface MenuState {
  activeItem: string;
  expandedItems: string[];
  isSidebarOpen: boolean;
  setActiveItem: (itemId: string) => void;
  setExpandedItems: (itemIds: string[]) => void;
  toggleExpanded: (itemId: string) => void;
  setSidebarOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  resetMenu: () => void;
}

export const useMenuStore = create<MenuState>()(
  persist(
    (set, get) => ({
      activeItem: '',
      expandedItems: [],
      isSidebarOpen: true,

      setActiveItem: (itemId: string) => {
        // ZUSTAND: 활성 메뉴 아이템 설정
        set({ activeItem: itemId });
      },

      setExpandedItems: (itemIds: string[]) => {
        set({ expandedItems: itemIds });
      },

      toggleExpanded: (itemId: string) => {
        // ZUSTAND: 메뉴 확장/축소 토글
        const { expandedItems } = get();
        if (expandedItems.includes(itemId)) {
          // 닫을 때는 해당 아이템과 모든 하위 아이템들을 제거
          set({
            expandedItems: expandedItems.filter(id => !id.startsWith(itemId)),
          });
        } else {
          // 열 때는 해당 아이템만 추가
          set({
            expandedItems: [...expandedItems, itemId],
          });
        }
      },

      setSidebarOpen: (isOpen: boolean) => {
        set({ isSidebarOpen: isOpen });
      },

      toggleSidebar: () => {
        // ZUSTAND: 사이드바 열기/닫기 토글
        set(state => ({ isSidebarOpen: !state.isSidebarOpen }));
      },

      resetMenu: () => {
        // ZUSTAND: 메뉴 상태 초기화
        set({ activeItem: '', expandedItems: [] });
      },
    }),
    {
      name: 'menu-storage',
      partialize: state => ({
        activeItem: state.activeItem,
        expandedItems: state.expandedItems,
      }),
    }
  )
);

// Development helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__MENU_STORE__ = useMenuStore;

  (window as any).getMenuState = () => {
    const state = useMenuStore.getState();
    console.log('🔍 Menu Store State:', {
      activeItem: state.activeItem,
      expandedItems: state.expandedItems,
      isSidebarOpen: state.isSidebarOpen,
    });
    return state;
  };

  (window as any).watchMenuStore = () => {
    console.log('👀 Watching menu store changes...');
    return useMenuStore.subscribe(state => {
      console.log('🔄 [Menu Store Update]', {
        activeItem: state.activeItem,
        expandedCount: state.expandedItems.length,
        sidebarOpen: state.isSidebarOpen,
      });
    });
  };

  // console.log('🛠️ Menu Store Debug Tools Available:');
  // console.log('  - getMenuState() : 현재 메뉴 상태 확인');
  // console.log('  - watchMenuStore() : 실시간 변화 감지');
  // console.log('  - __MENU_STORE__.getState() : 전체 상태');
}
